<template>
  <div class="odontogram-container">
    <div class="dental-chart">
      <!-- Arco Superior -->
      <div class="arch upper-arch">
        <div class="arch-label">Superior</div>
        <div class="teeth-row">
          <div
            v-for="tooth in upperTeeth"
            :key="tooth.id"
            class="tooth-wrapper"
          >
            <div
              class="tooth"
              :class="{
                'tooth-selected':
                  selectedTooth && selectedTooth.id === tooth.id,
              }"
              @click="selectTooth(tooth)"
            >
              <div class="tooth-number">
                {{ tooth.numero }}
              </div>
              <div class="tooth-faces">
                <div
                  class="face oclusal"
                  :class="{ active: tooth.faces.oclusal.ativo }"
                  :style="getFaceStyle(tooth.faces.oclusal, 'oclusal')"
                  @click.stop="editFace(tooth, 'oclusal')"
                />
                <div
                  class="face vestibular"
                  :class="{ active: tooth.faces.vestibular.ativo }"
                  :style="getFaceStyle(tooth.faces.vestibular, 'vestibular')"
                  @click.stop="editFace(tooth, 'vestibular')"
                />
                <div
                  class="face lingual"
                  :class="{ active: tooth.faces.lingual.ativo }"
                  :style="getFaceStyle(tooth.faces.lingual, 'lingual')"
                  @click.stop="editFace(tooth, 'lingual')"
                />
                <div
                  class="face mesial"
                  :class="{ active: tooth.faces.mesial.ativo }"
                  :style="getFaceStyle(tooth.faces.mesial, 'mesial')"
                  @click.stop="editFace(tooth, 'mesial')"
                />
                <div
                  class="face distal"
                  :class="{ active: tooth.faces.distal.ativo }"
                  :style="getFaceStyle(tooth.faces.distal, 'distal')"
                  @click.stop="editFace(tooth, 'distal')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Divisor -->
      <div class="arch-divider" />

      <!-- Arco Inferior -->
      <div class="arch lower-arch">
        <div class="arch-label">Inferior</div>
        <div class="teeth-row">
          <div
            v-for="tooth in lowerTeeth"
            :key="tooth.id"
            class="tooth-wrapper"
          >
            <div
              class="tooth"
              :class="{
                'tooth-selected':
                  selectedTooth && selectedTooth.id === tooth.id,
              }"
              @click="selectTooth(tooth)"
            >
              <div class="tooth-number">
                {{ tooth.numero }}
              </div>
              <div class="tooth-faces">
                <div
                  class="face oclusal"
                  :class="{ active: tooth.faces.oclusal.ativo }"
                  :style="getFaceStyle(tooth.faces.oclusal, 'oclusal')"
                  @click.stop="editFace(tooth, 'oclusal')"
                />
                <div
                  class="face vestibular"
                  :class="{ active: tooth.faces.vestibular.ativo }"
                  :style="getFaceStyle(tooth.faces.vestibular, 'vestibular')"
                  @click.stop="editFace(tooth, 'vestibular')"
                />
                <div
                  class="face lingual"
                  :class="{ active: tooth.faces.lingual.ativo }"
                  :style="getFaceStyle(tooth.faces.lingual, 'lingual')"
                  @click.stop="editFace(tooth, 'lingual')"
                />
                <div
                  class="face mesial"
                  :class="{ active: tooth.faces.mesial.ativo }"
                  :style="getFaceStyle(tooth.faces.mesial, 'mesial')"
                  @click.stop="editFace(tooth, 'mesial')"
                />
                <div
                  class="face distal"
                  :class="{ active: tooth.faces.distal.ativo }"
                  :style="getFaceStyle(tooth.faces.distal, 'distal')"
                  @click.stop="editFace(tooth, 'distal')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Edição -->
    <b-modal v-model="showModal" title="Editar Dente" hide-footer>
      <div v-if="editedTooth">
        <h5 class="mb-4">Dente {{ editedTooth.numero }}</h5>

        <b-form-group v-for="(face, key) in editedTooth.faces" :key="key">
          <b-form-checkbox v-model="face.ativo" :value="true" class="mb-2">
            {{ getFaceName(key) }}
          </b-form-checkbox>

          <b-form-textarea
            v-if="face.ativo"
            v-model="face.descricao"
            :placeholder="'Observações para ' + getFaceName(key).toLowerCase()"
            rows="2"
          />
        </b-form-group>

        <b-form-group label="Observações Gerais" class="mt-3">
          <b-form-textarea
            v-model="editedTooth.observacoes"
            placeholder="Observações gerais sobre o dente"
            rows="3"
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <b-button variant="secondary" class="mr-2" @click="showModal = false">
            Cancelar
          </b-button>
          <b-button variant="primary" @click="saveChanges"> Salvar </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BFormGroup,
  BFormCheckbox,
  BFormTextarea,
  BModal,
} from "bootstrap-vue";

export default {
  name: "DigitalOdontogram",
  components: {
    BButton,
    BFormGroup,
    BFormCheckbox,
    BFormTextarea,
    BModal,
  },
  props: {
    teeth: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      showModal: false,
      selectedTooth: null,
      editedTooth: null,
    };
  },
  computed: {
    upperTeeth() {
      return this.teeth
        .filter((tooth) => {
          const num = parseInt(tooth.numero, 10);
          return num >= 11 && num <= 28;
        })
        .sort((a, b) => parseInt(b.numero, 10) - parseInt(a.numero, 10)) // invertido
    },
    lowerTeeth() {
      return this.teeth
        .filter(tooth => {
          const num = parseInt(tooth.numero, 10);
          return num >= 31 && num <= 48;
        })
        .sort((a, b) => parseInt(a.numero, 10) - parseInt(b.numero, 10)); // ordem certa
    },
  },
  methods: {
    selectTooth(tooth) {
      this.selectedTooth = JSON.parse(JSON.stringify(tooth));
      this.editedTooth = JSON.parse(JSON.stringify(tooth));
      this.showModal = true;
    },
    editFace(tooth) {
      this.selectTooth(tooth);
      // opcional: já focar na face clicada
    },
    saveChanges() {
      this.$emit("tooth-updated", this.editedTooth);
      this.showModal = false;
      this.selectedTooth = null;
      this.editedTooth = null;
    },
    getFaceName(faceKey) {
      const faces = {
        oclusal: "Oclusal",
        vestibular: "Vestibular",
        lingual: "Lingual",
        mesial: "Mesial",
        distal: "Distal",
      };
      return faces[faceKey] || faceKey;
    },
    getFaceStyle(face, faceKey) {
      const defaultColors = {
        oclusal: "#ffc107",
        vestibular: "#17a2b8",
        lingual: "#28a745",
        mesial: "#dc3545",
        distal: "#6c757d",
      };

      return {
        backgroundColor: face.ativo
          ? face.corHex || defaultColors[faceKey]
          : "#f8f9fa",
        border: "1px solid #dee2e6",
      };
    },
  },
};
</script>

<style scoped>
.odontogram-container {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  max-width: 100%;
  overflow-x: auto;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.dental-chart {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 800px;
}

.arch {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.upper-arch {
  border-bottom: 3px solid #007bff;
}

.lower-arch {
  border-top: 3px solid #007bff;
}

.arch-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.teeth-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tooth-wrapper {
  position: relative;
  width: 65px;
  height: 110px;
  margin: 0 1px;
}

.tooth {
  width: 100%;
  height: 100%;
  border: 2px solid #6c757d;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  padding-top: 8px;
}

.tooth:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  border-color: #007bff;
}

.tooth-selected {
  border: 3px solid #007bff;
  box-shadow: 0 0 0 0.3rem rgba(0, 123, 255, 0.25);
  background: linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%);
}

.tooth-number {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 8px;
  color: #212529;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 30px;
  text-align: center;
}

.tooth-faces {
  display: grid;
  grid-template-areas:
    '. oclusal .'
    'mesial vestibular distal'
    '. lingual .';
  grid-gap: 3px;
  width: 85%;
  height: 65%;
  flex-grow: 1;
}

.face {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.face:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.face.active {
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
  border-color: #0056b3;
}

.oclusal {
  grid-area: oclusal;
}

.vestibular {
  grid-area: vestibular;
}

.lingual {
  grid-area: lingual;
}

.mesial {
  grid-area: mesial;
}

.distal {
  grid-area: distal;
}

.arch-divider {
  height: 2px;
  background: linear-gradient(90deg, transparent, #007bff, transparent);
  margin: 10px 0;
}

@media (max-width: 1200px) {
  .dental-chart {
    min-width: 600px;
  }

  .tooth-wrapper {
    width: 55px;
    height: 95px;
  }

  .tooth-number {
    font-size: 1rem;
  }
}

@media (max-width: 768px) {
  .odontogram-container {
    padding: 10px;
  }

  .dental-chart {
    min-width: 100%;
    gap: 1rem;
  }

  .arch {
    padding: 10px;
  }

  .tooth-wrapper {
    width: 45px;
    height: 80px;
  }

  .tooth-number {
    font-size: 0.9rem;
    padding: 1px 4px;
    min-width: 25px;
  }

  .tooth-faces {
    grid-gap: 2px;
    width: 80%;
    height: 60%;
  }

  .arch-label {
    font-size: 1rem;
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .tooth-wrapper {
    width: 40px;
    height: 70px;
  }

  .tooth-number {
    font-size: 0.8rem;
  }

  .teeth-row {
    gap: 4px;
  }
}
</style>
